
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PDFTemplateUploader from '@/components/student-certification/certificate-templates/PDFTemplateUploader';
import { CertificateTemplate } from '@/types/certificateTemplate';

const EditTemplateUploadPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [pdfTemplate, setPdfTemplate] = useState<{ file: File, url: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!templateId) {
      toast.error('Template ID is missing');
      navigate('/certificate-templates');
      return;
    }
    
    // Load template from session storage first (if available from previous step)
    const storedTemplate = sessionStorage.getItem('editTemplate');
    if (storedTemplate) {
      try {
        const parsedTemplate = JSON.parse(storedTemplate);
        setTemplate(parsedTemplate);
        
        // If the template has a PDF, set it
        if (parsedTemplate.metadata?.isPdf && parsedTemplate.metadata?.pdfUrl) {
          setPdfTemplate({
            // We can't restore the actual File object, but we can create a placeholder
            file: new File([], 'template.pdf'),
            url: parsedTemplate.metadata.pdfUrl
          });
        }
      } catch (error) {
        console.error('Error parsing stored template:', error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('No template data found. Please start from the beginning.');
      navigate(`/certificate-templates/edit/${templateId}/basic-info`);
    }
  }, [templateId, navigate]);

  const handlePdfUploaded = (file: File, previewUrl: string) => {
    setPdfTemplate({ file, url: previewUrl });
    
    // Update template in session storage
    if (template) {
      const updatedTemplate = {
        ...template,
        metadata: {
          ...(template.metadata || {}),
          isPdf: true,
          pdfUrl: previewUrl
        }
      };
      
      setTemplate(updatedTemplate);
      sessionStorage.setItem('editTemplate', JSON.stringify(updatedTemplate));
    }
    
    toast.success('PDF template uploaded successfully');
  };

  const handlePrevious = () => {
    if (templateId) {
      navigate(`/certificate-templates/edit/${templateId}/basic-info`);
    } else {
      navigate('/certificate-templates');
    }
  };

  const handleNext = () => {
    if (templateId) {
      navigate(`/certificate-templates/edit/${templateId}/mapping`);
    }
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Edit Certificate Template - Upload PDF | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Edit Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 2: Upload or update PDF template (optional)
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              2
            </div>
            <span className="text-sm mt-1 text-primary font-medium">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              3
            </div>
            <span className="text-sm mt-1">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Upload PDF Template</CardTitle>
          <CardDescription>
            {pdfTemplate ? 'Your PDF template is uploaded. You can replace it or continue to the next step.' : 'Upload a PDF file to use as the base for your certificate. You can add dynamic text fields in the next step.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pdfTemplate ? (
            <div className="border rounded-md p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="font-medium">{pdfTemplate.file.name || 'Certificate Template'}</p>
                    <p className="text-sm text-muted-foreground">
                      {pdfTemplate.file.size > 0 ? `${(pdfTemplate.file.size / 1024).toFixed(1)} KB` : ''}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    setPdfTemplate(null);
                    // Update template in session storage
                    if (template) {
                      const updatedTemplate = {
                        ...template,
                        metadata: {
                          ...(template.metadata || {}),
                          isPdf: false,
                          pdfUrl: undefined
                        }
                      };
                      
                      setTemplate(updatedTemplate);
                      sessionStorage.setItem('editTemplate', JSON.stringify(updatedTemplate));
                    }
                  }}
                >
                  Remove
                </Button>
              </div>
              <div className="h-[500px] border rounded overflow-hidden">
                <iframe
                  src={`${pdfTemplate.url}#toolbar=0`}
                  className="w-full h-full"
                  title="PDF Certificate Template"
                />
              </div>
            </div>
          ) : (
            <PDFTemplateUploader onPDFUploaded={handlePdfUploaded} />
          )}
          
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handlePrevious}>
                <ChevronLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
            </div>
            <Button type="button" onClick={handleNext}>
              {pdfTemplate ? 'Continue' : 'Skip'} <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditTemplateUploadPage;
